import './ConfirmActivationModal.less';

import React from 'react';
import { Modal, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { formatCurrency, getCreditsMonthly } from '../PreviewInvoice/helpers';

const { Text, Title } = Typography;

interface ConfirmActivationModalProps {
  open: boolean;
  priceId: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const ConfirmActivationModal: React.FC<ConfirmActivationModalProps> = ({
  open,
  priceId,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const { products } = useSelector((state: RootState) => state.products);

  // Find the price and product from the priceId
  const priceData = React.useMemo(() => {
    for (const product of products) {
      const price = product.prices.find((p) => p.id === priceId);
      if (price) {
        return {
          product,
          price,
        };
      }
    }
    return null;
  }, [products, priceId]);

  if (!priceData) {
    return null;
  }

  const { product, price } = priceData;
  const interval = price.recurring?.interval || 'month';
  const intervalLabel = interval === 'year' ? 'year' : 'month';
  const amount = formatCurrency(price.unit_amount, price.currency);
  const creditsMonthly = getCreditsMonthly(price, product);

  return (
    <Modal
      className="confirm-activation-modal"
      title="Confirm your subscription"
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      okText="Confirm & Activate"
      okButtonProps={{ loading }}
      cancelText="Cancel"
      width={480}
    >
      <div className="confirm-activation-content">
        <Title level={5} className="plan-name">
          {product.name}
        </Title>
        {creditsMonthly && (
          <Text type="secondary" className="plan-credits">
            {creditsMonthly.toLocaleString()} credits/month
          </Text>
        )}

        <div className="charge-info">
          <Text className="charge-label">You will be charged:</Text>
          <div className="charge-amount">
            <span className="amount">{amount}</span>
            <span className="interval"> per {intervalLabel}</span>
          </div>
          {interval === 'year' && (
            <Text type="secondary" className="monthly-equivalent">
              (
              {formatCurrency(
                Math.round(price.unit_amount / 12),
                price.currency
              )}
              /month)
            </Text>
          )}
        </div>

        <Text type="secondary" className="confirmation-note">
          Your payment method will be charged immediately upon confirmation.
        </Text>
      </div>
    </Modal>
  );
};

export default ConfirmActivationModal;
